import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { FilterUtils, PaginationUtils } from '@ocean/utils'
import { Card } from 'types/card'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getDistanceCourses } from 'modules/trainingLibrary/DistanceCourses/api/distanceCoursesService'
import {
  DistanceCoursesFilterItem,
  DistanceCoursesFilterItems,
} from 'modules/trainingLibrary/DistanceCourses/types'
import { DistanceCoursesState, ApiDistanceCoursesResponse } from './types'

const initialState: DistanceCoursesState = {
  distanceCoursesData: {},
  distanceCoursesFilters: {
    isCompleted: { keyType: FilterKeyType.Boolean, searchKey: 'result.isCompleted' },
  },
}

const distanceCoursesSlice = createSlice({
  name: 'distanceCourses',
  initialState,
  reducers: {
    distanceCoursesDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiDistanceCoursesResponse>>
    ) {
      state.distanceCoursesData = action.payload
    },
    setDistanceCoursesFilters(state, action: PayloadAction<DistanceCoursesFilterItem>) {
      const filter = state.distanceCoursesFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { distanceCoursesDataLoaded, setDistanceCoursesFilters } = distanceCoursesSlice.actions

export { setDistanceCoursesFilters }
export default distanceCoursesSlice.reducer

export const getFilteredDistanceCourses = createSelector(
  (distanceCoursesCards: Card[] = [], filters: DistanceCoursesFilterItems) => ({
    distanceCoursesCards,
    filters,
  }),
  ({ distanceCoursesCards, filters }) => FilterUtils.filterData(filters, distanceCoursesCards)
)

export const getDistanceCoursesPageItems = createSelector(
  (
    distanceCoursesCards: Card[] = [],
    filters: DistanceCoursesFilterItems,
    currentPageId: number,
    currentPageSize: number
  ) => ({
    filteredDistanceCoursesCards: getFilteredDistanceCourses(distanceCoursesCards, filters),
    currentPageId,
    currentPageSize,
  }),
  ({ filteredDistanceCoursesCards, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<Card>(filteredDistanceCoursesCards, currentPageId, currentPageSize)
)

export const loadDistanceCoursesData = () => {
  return createApiAction({
    apiCalls: {
      distanceCourses: getDistanceCourses,
    },
    payloadAction: distanceCoursesDataLoaded,
  })
}
